import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Payment from "@/pages/Payment";
import PaymentCancel from "@/pages/PaymentCancel";
import ProfileSetup from "@/pages/ProfileSetup";
import AgentDashboard from "@/pages/AgentDashboard";
import Quotations from "@/pages/Quotations";
import QuotationBuilder from "@/pages/QuotationBuilder";
import QuotationTemplateChooser from "@/pages/QuotationTemplateChooser";
import QuotationPreview from "@/pages/QuotationPreview";
import Vouchers from "@/pages/Vouchers";
import VoucherBuilder from "@/pages/VoucherBuilder";
import Invoices from "@/pages/Invoices";
import InvoiceBuilder from "@/pages/InvoiceBuilder";
import InvoiceView from "@/pages/InvoiceView";
import Customers from "@/pages/Customers";
import Reports from "@/pages/Reports";
import PamphletBuilder from "@/pages/PamphletBuilder";
import ForgotPassword from "@/pages/ForgotPassword";
import CustomerForm from "@/pages/CustomerForm";
import CustomerProfile from "@/pages/CustomerProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAgents from "@/pages/AdminAgents";
import AdminAgentDetail from "@/pages/AdminAgentDetail";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminActivity from "@/pages/AdminActivity";
import Suggestions from "@/pages/Suggestions";
import AdminSuggestions from "@/pages/AdminSuggestions";
import AdminHotelCategories from "@/pages/AdminHotelCategories";
import PublicQuotation from "@/pages/PublicQuotation";
import AgentHeartbeat from "@/components/AgentHeartbeat";
import "@/App.css";

function Protected({ children, role }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  // Admins landing on agent-only routes get redirected to admin panel
  if (!role && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <div className="App font-body">
        <Toaster richColors position="bottom-right" />
        <BrowserRouter>
          <AgentHeartbeat />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/q/:token" element={<PublicQuotation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/payment" element={<Protected><Payment /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
            <Route path="/profile/setup" element={<Protected><ProfileSetup /></Protected>} />
            <Route path="/dashboard" element={<Protected><AgentDashboard /></Protected>} />
            <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
            <Route path="/quotations/templates" element={<Protected><QuotationTemplateChooser /></Protected>} />
            <Route path="/quotations/new" element={<Protected><QuotationBuilder /></Protected>} />
            <Route path="/quotations/:id/edit" element={<Protected><QuotationBuilder /></Protected>} />
            <Route path="/quotations/:id/preview" element={<Protected><QuotationPreview /></Protected>} />
            <Route path="/vouchers" element={<Protected><Vouchers /></Protected>} />
            <Route path="/vouchers/new" element={<Protected><VoucherBuilder /></Protected>} />
            <Route path="/vouchers/:id/edit" element={<Protected><VoucherBuilder /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/invoices/new" element={<Protected><InvoiceBuilder /></Protected>} />
            <Route path="/invoices/:id" element={<Protected><InvoiceView /></Protected>} />
            <Route path="/invoices/:id/edit" element={<Protected><InvoiceBuilder /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/customers/new" element={<Protected><CustomerForm /></Protected>} />
            <Route path="/customers/:id" element={<Protected><CustomerProfile /></Protected>} />
            <Route path="/customers/:id/edit" element={<Protected><CustomerForm /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/pamphlets" element={<Protected><PamphletBuilder /></Protected>} />
            <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
            <Route path="/admin/agents" element={<Protected role="admin"><AdminAgents /></Protected>} />
            <Route path="/admin/agents/:id" element={<Protected role="admin"><AdminAgentDetail /></Protected>} />
            <Route path="/admin/subscriptions" element={<Protected role="admin"><AdminSubscriptions /></Protected>} />
            <Route path="/admin/activity" element={<Protected role="admin"><AdminActivity /></Protected>} />
            <Route path="/admin/suggestions" element={<Protected role="admin"><AdminSuggestions /></Protected>} />
            <Route path="/admin/hotel-categories" element={<Protected role="admin"><AdminHotelCategories /></Protected>} />
            <Route path="/suggestions" element={<Protected role="agent"><Suggestions /></Protected>} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}
